import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Check, ArrowRight, Pen, Heart, Package, Star } from "lucide-react";
import { HERO_CENNIK } from "@/components/heroBg";
import { WhatsAppIcon } from "@/components/PageCTA";

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay, ease: [0.25, 0.46, 0.45, 0.94] }} className={className}>
      {children}
    </motion.div>
  );
};

const tabs = [
  { key: "web", label: "Strony WWW" },
  { key: "graphics", label: "Grafiki hodowlane" },
  { key: "branding", label: "Logo i branding" },
  { key: "materials", label: "Materiały dodatkowe" },
];

const values = [
  { icon: Pen, label: "Indywidualne podejście" },
  { icon: Heart, label: "Doświadczenie" },
  { icon: Package, label: "Kompleksowa obsługa" },
  { icon: Star, label: "Wysoka jakość" },
];

/* ─── STRONY WWW ─── */
const webPackages = [
  {
    name: "Start", subtitle: "Strona na dobry początek", price: "od 1 900 zł", highlighted: false,
    features: ["do 5 podstron", "indywidualny projekt graficzny", "wersja mobilna", "galeria zdjęć", "formularz kontaktowy", "podstawowa optymalizacja SEO", "konfiguracja domeny i hostingu", "wsparcie po publikacji"],
    btn: "Wybieram Start",
  },
  {
    name: "Hodowla", subtitle: "Kompletna strona hodowli", price: "od 2 900 zł", highlighted: true,
    features: ["do 10 podstron", "indywidualny projekt graficzny", "profile psów i mioty", "galerie zdjęć", "blog lub aktualności", "formularz kontaktowy", "panel do samodzielnej edycji", "SEO + integracja z social mediami", "wsparcie po uruchomieniu"],
    btn: "Wybieram Hodowlę",
  },
  {
    name: "Premium", subtitle: "Rozbudowana strona premium", price: "od 4 900 zł", highlighted: false,
    features: ["do 15 podstron", "indywidualny projekt premium", "rozbudowane profile psów", "galerie zdjęć i filmów", "blog i aktualności", "panel administracyjny", "rozszerzona optymalizacja SEO", "możliwość wdrożenia wersji językowych", "priorytetowe wsparcie po publikacji"],
    btn: "Wybieram Premium",
  },
];

const modules = [
  ["Dodatkowa podstrona", "od … zł"], ["Profil psa", "od … zł"], ["Profil miotu", "od … zł"],
  ["Rodowód", "od … zł"], ["Rozbudowana galeria", "od … zł"], ["Blog lub aktualności", "od … zł"],
  ["FAQ", "od … zł"], ["Formularz rezerwacji szczenięcia", "od … zł"], ["Ankieta dla przyszłych nabywców", "od … zł"],
  ["Licznik wieku psa", "od … zł"], ["Przycisk WhatsApp lub Messenger", "od … zł"], ["Rozbudowana nawigacja", "od … zł"],
  ["Integracja z zewnętrznym systemem", "wycena indywidualna"],
];
const languages = [
  ["Wdrożenie drugiej wersji językowej", "od … zł"], ["Każdy kolejny język", "od … zł"],
  ["Wprowadzenie gotowych tłumaczeń klienta", "od … zł"], ["Przygotowanie tłumaczenia", "wycena indywidualna"],
];
const contentWriting = [
  ["Tekst na jedną podstronę", "od … zł"], ["Redakcja tekstu klienta", "od … zł"],
  ["Opis hodowli lub firmy", "od … zł"], ["Opis psa lub reproduktora", "od … zł"],
  ["Opis miotu", "od … zł"], ["Artykuł blogowy", "od … zł"], ["Pakiet tekstów na całą stronę", "wycena indywidualna"],
];
const extraWork = [
  ["Przeniesienie treści ze starej strony", "od … zł"], ["Dodanie nowego psa", "od … zł"],
  ["Dodanie nowego miotu", "od … zł"], ["Publikacja aktualności", "od … zł"],
  ["Wprowadzenie artykułu blogowego", "od … zł"], ["Dodanie galerii", "od … zł"],
  ["Większe zmiany programistyczne", "wycena indywidualna"], ["Realizacja ekspresowa", "dopłata 30%"],
];

/* ─── GRAFIKI ─── */
const graphicsItems = [
  { title: "Zapowiedź miotu", price: "od … zł", img: "/images/hodowlane/01_zapowiedz_miotu.webp" },
  { title: "Social media", price: "od … zł", img: "/images/hodowlane/02_social_media.webp" },
  { title: "Ogłoszenia i posty", price: "od … zł", img: "/images/hodowlane/03_ogloszenia_i_posty.webp" },
  { title: "Wizytówki", price: "od … zł", img: "/images/hodowlane/04_wizytowki.webp" },
  { title: "Plakaty i banery", price: "od … zł", img: "/images/hodowlane/05_plakaty_i_banery.webp" },
  { title: "Obróbka zdjęć", price: "od … zł", img: "/images/hodowlane/06_obrobka_zdjec.webp" },
];

/* ─── BRANDING ─── */
const brandingItems = [
  { title: "Logo podstawowe", price: "od … zł" },
  { title: "Logo + identyfikacja", price: "od … zł" },
  { title: "Księga znaku", price: "od … zł" },
  { title: "Rebranding", price: "od … zł" },
];

/* ─── MATERIAŁY ─── */
const materialsItems = [
  { title: "Koszulka z nadrukiem", price: "od … zł", img: "/images/products/tshirt.png" },
  { title: "Kubek z nadrukiem", price: "od … zł", img: "/images/products/mug.png" },
  { title: "Torba bawełniana", price: "od … zł", img: "/images/products/bag.png" },
  { title: "Teczka na dokumenty", price: "od … zł", img: "/images/portfolio/book.png" },
  { title: "Baner na płot", price: "od … zł", img: "/images/portfolio/banner.png" },
  { title: "Bluza z nadrukiem", price: "od … zł", img: "/images/products/mockup-7.jpg" },
];

/* ─── PRICE TABLE ─── */
const PriceTable = ({ num, title, desc, data }) => (
  <FadeIn>
    <div className="mb-12">
      <h3 className="font-heading text-[clamp(22px,2.5vw,28px)] text-[var(--color-text-dark)] mb-2">
        <span className="text-[var(--color-gold)] font-body text-sm font-semibold uppercase tracking-wider mr-3">{num}.</span>{title}
      </h3>
      {desc && <p className="text-sm font-body text-[var(--color-text-muted)] mb-4 max-w-2xl">{desc}</p>}
      <div className="border border-[var(--color-border-light)] rounded overflow-hidden">
        {data.map(([name, price], idx) => (
          <div key={idx} className={`flex justify-between items-center px-6 py-3.5 font-body text-sm ${idx % 2 === 0 ? 'bg-[var(--color-white)]' : 'bg-[var(--color-cream)]'}`}>
            <span className="text-[var(--color-text-dark)]">{name}</span>
            <span className="text-[var(--color-text-muted)] font-medium text-right whitespace-nowrap ml-4">{price}</span>
          </div>
        ))}
      </div>
    </div>
  </FadeIn>
);

/* ─── TAB: STRONY WWW ─── */
const WebTab = () => (
  <div>
    <FadeIn className="mb-8">
      <h3 className="font-heading text-[clamp(28px,3vw,36px)] text-[var(--color-text-dark)]">Strony internetowe</h3>
      <p className="text-sm font-body text-[var(--color-text-muted)] mt-2">Wybierz pakiet dopasowany do Twoich potrzeb i budżetu.</p>
    </FadeIn>
    <div className="grid md:grid-cols-3 gap-5">
      {webPackages.map((pkg, idx) => (
        <FadeIn key={idx} delay={idx * 0.08}>
          <div className={`pricing-card flex flex-col h-full ${pkg.highlighted ? "pricing-card--featured" : ""}`} data-testid={`pricing-${idx}`}>
            {pkg.highlighted && <span className="pricing-badge mb-3">Najczęściej wybierany</span>}
            <h4 className="font-body text-xs font-bold uppercase tracking-wider text-[var(--color-text-dark)]">{pkg.name}</h4>
            <p className="text-[11px] font-body text-[var(--color-text-muted)] mt-1">{pkg.subtitle}</p>
            <div className="pricing-price mt-3 mb-4">{pkg.price}</div>
            <p className="text-[10px] font-body font-bold uppercase tracking-wider text-[var(--color-text-muted)] mb-3">Pakiet zawiera:</p>
            <ul className="space-y-1.5 flex-1">
              {pkg.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-[var(--color-gold)] mt-0.5 flex-shrink-0" />
                  <span className="text-xs font-body text-[var(--color-text-muted)]">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/kontakt" className="mt-6">
              <button className={`w-full justify-center ${pkg.highlighted ? "button-primary" : "button-outline text-[var(--color-text-dark)]"}`}>{pkg.btn}</button>
            </Link>
          </div>
        </FadeIn>
      ))}
    </div>

    {/* Tabele cenowe */}
    <div className="mt-16">
      <PriceTable num="02" title="Dodatkowe moduły" data={modules} />
      <PriceTable num="03" title="Wersje językowe" desc="Cena zależy od liczby podstron i ilości treści." data={languages} />
      <PriceTable num="04" title="Pisanie treści na stronę" desc="Nie masz gotowych tekstów? Możemy przygotować je za Ciebie." data={contentWriting} />
      <PriceTable num="05" title="Dodatkowe prace" data={extraWork} />
      <FadeIn>
        <div className="mb-12">
          <h3 className="font-heading text-[clamp(22px,2.5vw,28px)] text-[var(--color-text-dark)] mb-2">
            <span className="text-[var(--color-gold)] font-body text-sm font-semibold uppercase tracking-wider mr-3">06.</span>Opieka nad stroną
          </h3>
          <p className="text-sm font-body text-[var(--color-text-muted)] mb-4">Po publikacji możemy aktualizować psy, mioty, zdjęcia, treści i informacje.</p>
          <div className="border border-[var(--color-border-light)] rounded overflow-hidden">
            <div className="flex justify-between items-center px-6 py-3.5 font-body text-sm bg-[var(--color-white)]"><span className="text-[var(--color-text-dark)]">Pojedyncza aktualizacja</span><span className="text-[var(--color-text-muted)] font-medium">od … zł</span></div>
            <div className="flex justify-between items-center px-6 py-3.5 font-body text-sm bg-[var(--color-cream)]"><span className="text-[var(--color-text-dark)]">Miesięczna opieka techniczna</span><span className="text-[var(--color-text-muted)] font-medium">od … zł</span></div>
            <div className="flex justify-between items-center px-6 py-3.5 font-body text-sm bg-[var(--color-white)]"><span className="text-[var(--color-text-dark)]">Stała obsługa i rozwój strony</span><span className="text-[var(--color-text-muted)] font-medium">wycena indywidualna</span></div>
          </div>
        </div>
      </FadeIn>
      <p className="text-xs font-body text-[var(--color-text-muted)] text-center max-w-2xl mx-auto">
        Podane ceny są cenami początkowymi. Ostateczna wycena zależy od liczby podstron, funkcjonalności, ilości treści, wersji językowych oraz materiałów dostarczonych przez klienta.
      </p>
    </div>
  </div>
);

/* ─── TAB: GRAFIKI ─── */
const GraphicsTab = () => (
  <div>
    <FadeIn className="mb-8">
      <h3 className="font-heading text-[clamp(28px,3vw,36px)] text-[var(--color-text-dark)]">Grafiki hodowlane</h3>
      <p className="text-sm font-body text-[var(--color-text-muted)] mt-2">Zapowiedzi miotów, prezentacje psów, social media i więcej.</p>
    </FadeIn>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
      {graphicsItems.map((g, idx) => (
        <FadeIn key={idx} delay={idx * 0.05}>
          <div className="product-card overflow-hidden group">
            <div className="aspect-square bg-[var(--color-cream)] overflow-hidden">
              <img src={g.img} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-4">
              <h4 className="text-xs font-body font-bold uppercase tracking-wider text-[var(--color-text-dark)]">{g.title}</h4>
              <p className="text-sm font-heading text-[var(--color-gold)] font-medium mt-1">{g.price}</p>
            </div>
          </div>
        </FadeIn>
      ))}
    </div>
  </div>
);

/* ─── TAB: BRANDING ─── */
const BrandingTab = () => (
  <div>
    <FadeIn className="mb-8">
      <h3 className="font-heading text-[clamp(28px,3vw,36px)] text-[var(--color-text-dark)]">Logo i branding</h3>
      <p className="text-sm font-body text-[var(--color-text-muted)] mt-2">Budowanie marki od logo po pełną identyfikację wizualną.</p>
    </FadeIn>
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
      {brandingItems.map((b, idx) => (
        <FadeIn key={idx} delay={idx * 0.05}>
          <div className="pricing-card text-center">
            <h4 className="font-heading text-xl font-medium text-[var(--color-text-dark)] mb-3">{b.title}</h4>
            <div className="pricing-price text-3xl">{b.price}</div>
            <Link to="/kontakt" className="mt-6 inline-block">
              <button className="button-outline text-[var(--color-text-dark)] text-xs">Zapytaj</button>
            </Link>
          </div>
        </FadeIn>
      ))}
    </div>
  </div>
);

/* ─── TAB: MATERIAŁY ─── */
const MaterialsTab = () => (
  <div>
    <FadeIn className="mb-8">
      <h3 className="font-heading text-[clamp(28px,3vw,36px)] text-[var(--color-text-dark)]">Materiały reklamowe</h3>
      <p className="text-sm font-body text-[var(--color-text-muted)] mt-2">Koszulki, kubki, torby, teczki i inne produkty z Twoim brandingiem.</p>
    </FadeIn>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
      {materialsItems.map((m, idx) => (
        <FadeIn key={idx} delay={idx * 0.05}>
          <div className="product-card overflow-hidden group">
            <div className="aspect-square bg-[var(--color-cream)] overflow-hidden">
              <img src={m.img} alt={m.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-4">
              <h4 className="text-xs font-body font-bold uppercase tracking-wider text-[var(--color-text-dark)]">{m.title}</h4>
              <p className="text-sm font-heading text-[var(--color-gold)] font-medium mt-1">{m.price}</p>
            </div>
          </div>
        </FadeIn>
      ))}
    </div>
    <FadeIn delay={0.3}>
      <div className="mt-8 bg-[var(--color-white)] border border-[var(--color-border-light)] rounded p-6 text-center">
        <p className="text-sm font-body text-[var(--color-text-muted)]">Produkcja wyceniana indywidualnie po wyborze produktu, wariantu i liczby sztuk.</p>
      </div>
    </FadeIn>
  </div>
);

/* ─── MAIN ─── */
const Pakiety = () => {
  const [activeTab, setActiveTab] = useState("web");

  const renderTabContent = () => {
    switch (activeTab) {
      case "web": return <WebTab />;
      case "graphics": return <GraphicsTab />;
      case "branding": return <BrandingTab />;
      case "materials": return <MaterialsTab />;
      default: return <WebTab />;
    }
  };

  return (
    <div className="min-h-screen" data-testid="pakiety-page">
      {/* Hero */}
      <div className="subpage-hero subpage-hero--bg flex items-end" style={HERO_CENNIK}>
        <div className="container pb-0">
          <p className="eyebrow mb-4">Cennik stron internetowych</p>
          <h1 className="font-heading text-[clamp(36px,5vw,56px)] text-[var(--color-text-light)] mb-4">
            Strona dopasowana do Twojej marki
          </h1>
          <p className="lead max-w-xl mb-6">
            Wybierz pakiet odpowiadający potrzebom Twojej hodowli lub firmy. Każdy projekt przygotowujemy indywidualnie.
          </p>
          <div className="flex flex-wrap gap-6 mb-6">
            <span className="font-body text-sm text-[var(--color-text-light-muted)]"><strong className="text-[var(--color-text-light)]">START</strong> — od 1 900 zł</span>
            <span className="font-body text-sm text-[var(--color-text-light-muted)]"><strong className="text-[var(--color-text-light)]">HODOWLA</strong> — od 2 900 zł</span>
            <span className="font-body text-sm text-[var(--color-text-light-muted)]"><strong className="text-[var(--color-text-light)]">PREMIUM</strong> — od 4 900 zł</span>
          </div>
          <Link to="/kontakt"><button className="button-primary">Zapytaj o wycenę <ArrowRight className="w-4 h-4" /></button></Link>
        </div>
      </div>

      {/* Tabs + Content */}
      <div className="section bg-[var(--color-cream)]">
        <div className="container">
          <div className="flex flex-wrap gap-1 mb-12 border-b border-[var(--color-border-light)]" data-testid="pricing-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 text-xs font-body font-semibold uppercase tracking-wider border-b-2 transition-all -mb-px ${
                  activeTab === tab.key
                    ? "border-[var(--color-gold)] text-[var(--color-gold)]"
                    : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-dark)]"
                }`}
                data-testid={`tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            {renderTabContent()}
          </motion.div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-[var(--color-black-soft)]" style={{ paddingBlock: 80 }}>
        <div className="container text-center max-w-2xl mx-auto">
          <FadeIn><h2 className="font-heading text-[clamp(28px,3.5vw,42px)] text-[var(--color-text-light)] mb-4">Stwórzmy stronę dopasowaną do Twojej marki</h2></FadeIn>
          <FadeIn delay={0.1}><p className="font-body text-[var(--color-text-light-muted)] text-sm mb-8 leading-relaxed">Opowiedz nam o swojej hodowli lub firmie. Dobierzemy najlepszy zakres, funkcje i pakiet, a następnie przygotujemy indywidualną wycenę.</p></FadeIn>
          <FadeIn delay={0.15}>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/kontakt"><button className="button-primary">Zapytaj o wycenę <ArrowRight className="w-4 h-4" /></button></Link>
              <a href="https://wa.me/48507334540" target="_blank" rel="noopener noreferrer"><button className="button-outline flex items-center gap-2"><WhatsAppIcon className="w-4 h-4" /> WhatsApp</button></a>
            </div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
};

export default Pakiety;
